<template>
  <article class="manage-field-row" :class="{ 'manage-field-row--archived': !item.active }">
    <div class="manage-field-row__identity">
      <button
        v-if="expandable"
        type="button"
        class="manage-field-row__expand"
        :class="{ 'manage-field-row__expand--open': expanded }"
        :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${item.name}`"
        :aria-expanded="String(expanded)"
        @click="$emit('toggle-expand')"
      >›</button>
      <button
        type="button"
        class="manage-field-row__color"
        :aria-label="`Change color for ${item.name}`"
        :aria-expanded="String(paletteOpen)"
        :disabled="working || readonly"
        @click.stop="paletteOpen = !paletteOpen"
      ><i :style="{ backgroundColor: item.color }"></i></button>
      <input
        v-model="localName"
        type="text"
        maxlength="96"
        :aria-label="`${singular} name`"
        :disabled="working || readonly"
        @keydown.enter.prevent="commitName"
        @blur="commitName"
      >
    </div>

    <div class="manage-field-row__context">
      <span>{{ context }}</span>
      <small v-if="childCount !== null">{{ childCount }} {{ childLabel }}</small>
    </div>

    <app-status-badge design="new" compact :label="item.active ? 'Active' : 'Archived'" :tone="item.active ? 'green' : 'neutral'" />

    <div class="manage-field-row__actions">
      <app-button v-if="addChildLabel && !readonly" design="new" :disabled="working || !item.active" @click.native="$emit('add-child')">{{ addChildLabel }}</app-button>
      <app-button v-if="!readonly" design="new" :theme="item.active ? 'error' : 'dark'" :disabled="working" @click.native="$emit('update', { active: !item.active })">
        {{ item.active ? 'Archive' : 'Restore' }}
      </app-button>
    </div>

    <div v-if="paletteOpen" class="manage-field-row__palette" @click.stop>
      <strong>Choose color</strong>
      <div role="list" :aria-label="`${singular} colors`">
        <button
          v-for="color in palette"
          :key="color"
          type="button"
          :style="{ backgroundColor: color }"
          :class="{ 'is-selected': normalizedColor(item.color) === normalizedColor(color) }"
          :aria-label="`Use color ${color}`"
          :disabled="working"
          @click="selectColor(color)"
        ></button>
      </div>
    </div>
  </article>
</template>

<script>
import { SUPPORT_TAG_COLORS } from './tagPalette'

export default {
  name: 'SupportManageDictionaryRow',
  props: {
    item: { type: Object, required: true },
    singular: { type: String, required: true },
    context: { type: String, default: '' },
    expandable: Boolean,
    expanded: Boolean,
    childCount: { type: Number, default: null },
    childLabel: { type: String, default: '' },
    addChildLabel: { type: String, default: '' },
    readonly: Boolean,
    working: Boolean
  },
  data() {
    return {
      localName: this.item.name,
      paletteOpen: false,
      palette: SUPPORT_TAG_COLORS
    }
  },
  watch: {
    'item.name'(value) { this.localName = value },
    working(value) { if (!value) this.localName = this.item.name }
  },
  mounted() { document.addEventListener('click', this.closePalette) },
  beforeDestroy() { document.removeEventListener('click', this.closePalette) },
  methods: {
    normalizedColor(color) { return (color || '').toUpperCase() },
    closePalette() { this.paletteOpen = false },
    commitName() {
      if (this.readonly) return
      const name = this.localName.trim()
      if (!name) { this.localName = this.item.name; return }
      if (name !== this.item.name && !this.working) this.$emit('update', { name })
    },
    selectColor(color) {
      if (this.readonly) return
      if (!this.working && this.normalizedColor(color) !== this.normalizedColor(this.item.color)) this.$emit('update', { color })
      this.paletteOpen = false
    }
  }
}
</script>

<style lang="scss" scoped>
.manage-field-row { position: relative; display: grid; min-width: 0; grid-template-columns: minmax(230px, 1.4fr) minmax(120px, .7fr) auto auto; align-items: center; gap: 14px; padding: 12px 14px; border: 1px solid $ui-line; border-radius: 12px; background: $ui-surface; transition: border-color .15s ease, opacity .15s ease; }
.manage-field-row:hover { border-color: $ui-line-strong; }
.manage-field-row--archived { background: rgba(255, 255, 255, .58); opacity: .7; }
.manage-field-row__identity { display: flex; min-width: 0; align-items: center; gap: 9px; }
.manage-field-row__expand { width: 26px; height: 26px; flex: 0 0 26px; padding: 0; border: 0; background: transparent; color: $ui-muted; cursor: pointer; font-size: 25px; line-height: 1; transform: rotate(0); transition: transform .15s ease; }
.manage-field-row__expand--open { transform: rotate(90deg); }
.manage-field-row__color { display: inline-grid; width: 30px; height: 30px; flex: 0 0 30px; padding: 0; place-items: center; border: 1px solid $ui-line; border-radius: 8px; background: $ui-surface; cursor: pointer; }
.manage-field-row__color i { width: 16px; height: 16px; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px rgba(16, 24, 40, .18); }
.manage-field-row__identity input { width: 100%; min-width: 0; height: 36px; padding: 0 9px; border: 1px solid $ui-line; border-radius: 8px; background: $ui-surface; color: $ui-ink; font: 600 $ui-font-size-control $ui-font-heading; }
.manage-field-row__identity input:hover { border-color: $ui-line-strong; background: $ui-surface-soft; }
.manage-field-row__identity input:focus { border-color: $ui-blue; outline: 0; background: $ui-surface; box-shadow: 0 0 0 3px rgba($ui-blue, .1); }
.manage-field-row__context { display: grid; min-width: 0; gap: 2px; color: $ui-muted; font-size: $ui-font-size-metadata; }
.manage-field-row__context span, .manage-field-row__context small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.manage-field-row__context small { font-size: 11px; }
.manage-field-row__actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.manage-field-row__actions :deep(.btn) { white-space: nowrap; }
.manage-field-row__color:disabled { cursor: wait; opacity: .5; }
.manage-field-row__palette { position: absolute; z-index: 20; top: calc(100% + 7px); left: 48px; width: 226px; padding: 12px; border: 1px solid $ui-line; border-radius: 12px; background: $ui-surface; box-shadow: 0 14px 36px rgba(28, 45, 74, .18); }
.manage-field-row__palette > strong { display: block; margin-bottom: 9px; color: $ui-ink; font-size: 12px; }
.manage-field-row__palette > div { display: grid; grid-template-columns: repeat(6, 24px); gap: 8px; }
.manage-field-row__palette button { width: 24px; height: 24px; padding: 0; border: 2px solid #fff; border-radius: 50%; cursor: pointer; box-shadow: 0 0 0 1px rgba(16, 24, 40, .14); }
.manage-field-row__palette button.is-selected { box-shadow: 0 0 0 2px $ui-ink; }
@media (max-width: 1200px) {
  .manage-field-row { grid-template-columns: minmax(0, 1fr) auto; }
  .manage-field-row__context { grid-column: 1; }
  .manage-field-row__actions { grid-column: 1 / -1; justify-content: flex-start; }
}
@media (max-width: 560px) {
  .manage-field-row { gap: 9px; padding: 11px; }
  .manage-field-row__identity { grid-column: 1 / -1; }
  .manage-field-row__palette { left: 10px; }
}
</style>
